import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";

export interface WatermarkItem {
  id: string;
  x?: number;
  y?: number;
  serverId?: string;
  alignEnable?: string;
  [key: string]: unknown;
}

interface PasteOptions {
  offsetPct?: number;
}

function makeDuplicate(original: WatermarkItem | null, opts?: PasteOptions): WatermarkItem {
  const offset = Math.max(0, Number(opts?.offsetPct ?? 5) || 0);
  const dup = cloneDeep(original || {}) as WatermarkItem;
  const newId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  dup.id = newId;
  // Duplicates should be treated as new items when saving a template.
  delete dup.serverId;

  // Keep them visible (slight offset), clamp to 0..95 to avoid running out of bounds.
  const x = Number.isFinite(Number(dup.x)) ? Number(dup.x) : 0;
  const y = Number.isFinite(Number(dup.y)) ? Number(dup.y) : 0;
  dup.x = Math.min(95, x + offset);
  dup.y = Math.min(95, y + offset);

  return dup;
}

interface WatermarksState {
  items: WatermarkItem[];
  activeId: string | null;
  clipboard: WatermarkItem | null;
  loaded: boolean;
  waterMarksloader: boolean;
  template: boolean;
  deletedWaterMarkIds: string[];
}

const initialState: WatermarksState = {
  items: [],
    activeId: "",
    clipboard: null,
    loaded: false,
    waterMarksloader: false,
    template: false,
  deletedWaterMarkIds: [],
};

export const waterMarks = createSlice({
  name: "WaterMarks",
  initialState,
  reducers: {
    setItem: (state, action: PayloadAction<WatermarkItem>) => {
      const new_data = action.payload;
      const updated_data = [...state.items, new_data];
      state.items = updated_data;
      state.activeId = action.payload.id;
    },
    setScreenShot: (state, action: PayloadAction<WatermarkItem>) => {
      state.items = [action.payload];
      // state.activeId = action.payload.id;
    },
    setTemplateData: (state, action: PayloadAction<WatermarkItem[]>) => {
      state.items = action.payload;
    },
    setWaterMarksLoader: (state, action: PayloadAction<boolean>) => {
      state.waterMarksloader = action.payload;
    },
    setActiveId: (state, action: PayloadAction<string>) => {
      state.activeId = action.payload;
    },
    changeItemProperties: (state, action: PayloadAction<Partial<WatermarkItem> & { id: string }>) => {
      const id = action.payload?.id;
      const idx = state.items.findIndex((it) => it.id === id);
      if (idx === -1) return;

      const current = cloneDeep(state.items[idx]);
      const next = { ...current, ...action.payload } as WatermarkItem;
      // Template/API payloads use `rotation`; editor uses `deg`. Merging only `deg` left stale `rotation`,
      // and encrypt used `deg || rotation` which treated 0° as falsy — keep a single source of truth.
      if (Object.prototype.hasOwnProperty.call(action.payload, "deg")) {
        delete (next as Record<string, unknown>).rotation;
      }

      // Preserve z-order by updating in place (do NOT move item to end).
      state.items[idx] = next;
    },
    setLoaded: (state, action: PayloadAction<boolean>) => {
      state.loaded = action.payload;
    },
    setTemplateLoaded: (state, action: PayloadAction<boolean>) => {
      state.template = action.payload;
    },
    copyItem: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const item = state.items.find((it) => it.id === id);
      state.clipboard = item ? cloneDeep(item) : null;
    },
    pasteClipboard: (state, action: PayloadAction<PasteOptions | undefined>) => {
      if (!state.clipboard) return;
      const duplicate = makeDuplicate(state.clipboard, action.payload);
      state.items = [...state.items, duplicate];
      state.activeId = duplicate.id;
    },
    duplicateItem: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const item = state.items.find((it) => it.id === id);
      if (!item) return;
      state.clipboard = cloneDeep(item);
      const duplicate = makeDuplicate(item, { offsetPct: 5 });
      state.items = [...state.items, duplicate];
      state.activeId = duplicate.id;
    },
    reorderItems: (state, action: PayloadAction<string[]>) => {
      const orderedIds = Array.isArray(action.payload) ? action.payload : [];
      if (!orderedIds.length) return;

      const map = new Map(state.items.map((it) => [it.id, it]));
      const next = orderedIds.map((id) => map.get(id)).filter(Boolean);

      // Preserve any items not present in orderedIds (shouldn't happen, but keep safe).
      const missing = state.items.filter((it) => !orderedIds.includes(it.id));
      state.items = [...next, ...missing];
    },
    deleteItem: (state, action: PayloadAction<{ id: string }>) => {
      const id = action.payload?.id;
      const existing = state.items.find((it) => it?.id === id);
      state.items = state.items.filter((item) => item.id !== id);

      // For existing templates, backend expects deletedIds to be serverIds.
      const deleteKey = existing?.serverId;
      if (deleteKey) {
        state.deletedWaterMarkIds = [...state.deletedWaterMarkIds, deleteKey];
      }
      // Clear activeId if the deleted item was the active one
      if (state.activeId === id) {
        state.activeId = "";
      }
    },
    deleteAll: (state) => {
      state.items = [];
      state.activeId = null;
      state.clipboard = null;
      state.loaded = false;
      state.template = false;
      state.deletedWaterMarkIds = [];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setItem,
  setScreenShot,
  setWaterMarksLoader,
  setActiveId,
  changeItemProperties,
  deleteItem,
  setTemplateData,
  deleteAll,
  setLoaded,
  setTemplateLoaded,
  reorderItems,
  copyItem,
  pasteClipboard,
  duplicateItem,
} = waterMarks.actions;

export default waterMarks.reducer;
